import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountLayout from '../components/AccountLayout';
import AccountPagination, { AccountPaginationMeta } from '../components/AccountPagination';
import AccountSupportDialog, { AccountSupportTarget } from '../components/AccountSupportDialog';
import { Order, getMemberOrders, orderStatusLabels, paymentStatusLabels } from '@/services/orders';

const defaultPagination: AccountPaginationMeta = { Page: 1, PerPage: 10, Total: 0, TotalPages: 1 };

const statusBadgeClass: Record<string, string> = {
  Pending: 'bg-[#FAF3E4] text-[#B5791F]',
  Processing: 'bg-[#E9F1FB] text-[#2A5CA8]',
  Shipped: 'bg-[#EFF7F1] text-[#1B3D2A]',
  Completed: 'bg-[#1B3D2A]/10 text-[#1B3D2A]',
  Cancelled: 'bg-[#fdebea] text-[#c3423f]',
};

export default function AccountOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [pagination, setPagination] = useState<AccountPaginationMeta>(defaultPagination);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [supportTarget, setSupportTarget] = useState<AccountSupportTarget | null>(null);

  function load(page: number) {
    setLoading(true);
    setError('');
    getMemberOrders(page, defaultPagination.PerPage)
      .then((data) => {
        setOrders(data.Orders);
        setPagination(data.Pagination);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : '訂單載入失敗。');
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load(1);
  }, []);

  return (
    <AccountLayout title="訂單查詢">
      {loading ? (
        <div className="rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] py-16 text-center text-sm text-[#9A8A7A]">載入中...</div>
      ) : error ? (
        <div className="rounded-2xl border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm text-[#c3423f]">{error}</div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] py-16 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white">
            <i className="ri-file-list-3-line text-2xl text-[#9A8A7A]" />
          </div>
          <p className="font-bold text-[#1B3D2A]">目前尚無訂單紀錄</p>
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="mt-5 rounded-xl bg-[#1B3D2A] px-6 py-3 text-sm font-bold text-white hover:bg-[#143026]"
          >
            前往選購
          </button>
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {orders.map((order) => (
              <div
                key={order.OrderId}
                className="rounded-2xl border border-[#E8E0D6] bg-white p-5 transition hover:border-[#1B3D2A]/40 hover:shadow-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold text-[#1B3D2A]">訂單編號 {order.OrderNumber}</p>
                    <p className="mt-0.5 text-xs text-[#9A8A7A]">{order.CreatedAt}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusBadgeClass[order.OrderStatus] || ''}`}>
                      {orderStatusLabels[order.OrderStatus]}
                    </span>
                    <span className="rounded-full bg-[#FAF8F5] px-3 py-1 text-xs font-bold text-[#5A6B5A]">
                      {paymentStatusLabels[order.PaymentStatus]}
                    </span>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[#E8E0D6] pt-3">
                  <p className="text-sm text-[#5A6B5A]">共 {order.Items.reduce((sum, item) => sum + item.Quantity, 0)} 件商品</p>
                  <p className="text-base font-bold text-[#1B3D2A]">NT$ {order.TotalAmount.toLocaleString()}</p>
                </div>
                <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setSupportTarget({
                      sourceType: 'Order',
                      sourceId: order.OrderId,
                      title: `訂單 ${order.OrderNumber}`,
                      description: `${orderStatusLabels[order.OrderStatus]} / ${paymentStatusLabels[order.PaymentStatus]}`,
                      category: 'Order',
                    })}
                    className="rounded-xl border border-[#1B3D2A]/20 px-4 py-2 text-sm font-bold text-[#1B3D2A] hover:bg-[#1B3D2A]/8"
                  >
                    詢問這筆訂單
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/account/orders/${order.OrderNumber}`)}
                    className="rounded-xl bg-[#1B3D2A] px-4 py-2 text-sm font-bold text-white hover:bg-[#143026]"
                  >
                    查看明細
                  </button>
                </div>
              </div>
            ))}
          </div>
          <AccountPagination disabled={loading} pagination={pagination} onChange={load} />
        </>
      )}
      <AccountSupportDialog target={supportTarget} onClose={() => setSupportTarget(null)} />
    </AccountLayout>
  );
}
